import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getMyLeagues, getMyLeagueScore, getUnreadCounts, markPoolRead } from '../services/api';
import { useAuth } from './AuthContext';
import { supabase, isSupabaseConfigured } from '../services/supabaseClient';
import { playChatAlert } from '../utils/chatAlerts';

const PoolContext = createContext(null);

const STORAGE_KEY = 'pnp_selected_pool_id';

// localStorage can throw (Firefox NS_ERROR_ABORT, blocked storage, private
// modes). These run during render, so a throw would take down the whole app.
const readStoredPoolId = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? parseInt(stored, 10) : null;
  } catch { return null; }
};

const writeStoredPoolId = (id) => {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, String(id));
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
};

export const usePool = () => {
  const context = useContext(PoolContext);
  if (!context) {
    throw new Error('usePool must be used within a PoolProvider');
  }
  return context;
};

export const PoolProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [pools, setPools] = useState([]);
  const [selectedPoolId, setSelectedPoolIdState] = useState(readStoredPoolId);
  const [scoresByPool, setScoresByPool] = useState({});
  const [unreadByPool, setUnreadByPool] = useState({});
  const [loading, setLoading] = useState(false);
  // Pool ID currently being viewed in Corner Chat — Realtime arrivals for
  // this pool auto-mark-read instead of incrementing the badge.
  const [activeChatPoolId, setActiveChatPoolId] = useState(null);
  const activeChatPoolIdRef = useRef(null);
  useEffect(() => { activeChatPoolIdRef.current = activeChatPoolId; }, [activeChatPoolId]);

  const loadPools = useCallback(async () => {
    if (!isAuthenticated) {
      setPools([]);
      setScoresByPool({});
      setUnreadByPool({});
      return;
    }
    setLoading(true);
    try {
      const res = await getMyLeagues();
      const list = res.data || [];
      setPools(list);

      // If no selection or selection no longer valid, default to first pool
      setSelectedPoolIdState((current) => {
        const valid = list.some((p) => p.id === current);
        if (valid) return current;
        const next = list[0]?.id ?? null;
        writeStoredPoolId(next);
        return next;
      });
    } catch (e) {
      setPools([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadPools();
  }, [loadPools, user?.id]);

  // Fetch scores for all pools so the dropdown can show each one's score inline
  useEffect(() => {
    if (!isAuthenticated || pools.length === 0) return;
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        pools.map(async (p) => {
          try {
            const res = await getMyLeagueScore(p.id);
            return [p.id, res.data];
          } catch (e) {
            return [p.id, null];
          }
        })
      );
      if (cancelled) return;
      const map = {};
      results.forEach(([id, score]) => {
        if (score) map[id] = score;
      });
      setScoresByPool(map);
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, pools]);

  // Initial unread counts on login / pools change
  const refreshUnread = useCallback(async () => {
    if (!isAuthenticated) {
      setUnreadByPool({});
      return;
    }
    try {
      const res = await getUnreadCounts();
      const map = {};
      (res.data || []).forEach((row) => { map[row.league_id] = row.count; });
      setUnreadByPool(map);
    } catch (e) {
      // Leave existing counts on failure
    }
  }, [isAuthenticated]);

  useEffect(() => { refreshUnread(); }, [refreshUnread, pools.length]);

  // Subscribe to Realtime INSERTs for every pool the user belongs to.
  // When a message arrives we either: bump the unread count for that pool,
  // OR auto-mark-read if it's the pool currently being viewed in chat.
  useEffect(() => {
    if (!isAuthenticated || !isSupabaseConfigured() || !user?.id || pools.length === 0) return;

    const myId = user.id;
    const myPoolIds = pools.map((p) => p.id);
    const channels = myPoolIds.map((leagueId) =>
      supabase
        .channel(`pool-unread-${leagueId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'pool_messages',
            filter: `league_id=eq.${leagueId}`,
          },
          (payload) => {
            const m = payload.new;
            // Don't badge or alert on our own messages
            if (m.user_id === myId) return;
            // Auto-read while this room is open — no alert, no badge
            if (activeChatPoolIdRef.current === leagueId) {
              markPoolRead(leagueId).catch(() => {});
              return;
            }
            setUnreadByPool((prev) => ({
              ...prev,
              [leagueId]: (prev[leagueId] || 0) + 1,
            }));
            // Soft chime + light haptic, gated by mute toggle and visibility
            playChatAlert();
          }
        )
        .subscribe()
    );

    return () => {
      channels.forEach((ch) => supabase.removeChannel(ch));
    };
  }, [isAuthenticated, user?.id, pools]);

  const setSelectedPoolId = useCallback((id) => {
    setSelectedPoolIdState(id);
    writeStoredPoolId(id);
  }, []);

  // Called by CornerChat when a room is opened or the active pool switches.
  // Bumps the server-side last_read_at and clears the local unread count.
  const markChatRead = useCallback(async (leagueId) => {
    if (!leagueId) return;
    setUnreadByPool((prev) => {
      if (!prev[leagueId]) return prev;
      const { [leagueId]: _drop, ...rest } = prev;
      return rest;
    });
    try { await markPoolRead(leagueId); } catch (e) { /* silent */ }
  }, []);

  const totalUnread = Object.values(unreadByPool).reduce((sum, n) => sum + (n || 0), 0);
  const selectedPool = pools.find((p) => p.id === selectedPoolId) || null;
  const selectedPoolScore = selectedPoolId ? scoresByPool[selectedPoolId] : null;

  const value = {
    pools,
    selectedPool,
    selectedPoolId,
    setSelectedPoolId,
    selectedPoolScore,
    scoresByPool,
    loading,
    refresh: loadPools,
    // Chat unread badges
    unreadByPool,
    totalUnread,
    refreshUnread,
    markChatRead,
    activeChatPoolId,
    setActiveChatPoolId,
  };

  return <PoolContext.Provider value={value}>{children}</PoolContext.Provider>;
};

export default PoolContext;
